/* eslint-disable complexity */
import { type SkeletonLoaderArea, SkeletonLoader } from '@toggle/design-system';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TextSection } from '~/components/text-section/TextSection';
import { ImpactedCompanies } from '~/modules/impacted-companies/ImpactedCompanies';
import { ThematicFocus } from '~/modules/thematic-focus/ThematicFocus';
import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';
import { useEntity } from '~/react-query-hooks/entities/useEntities';
import { useEarningsInsightsFilter } from '~/react-query-hooks/insights/useEarningsInsightsFilter';
import { useGetEarningsInsightRecap } from '~/react-query-hooks/insights/useGetEarningsInsightRecap';
import { useFlags } from '~/stores/use-flags/useFlags';
import { ScheduleDemoBanner } from '~/views/stream/components/schedule-demo-banner/ScheduleDemoBanner';
import { MarketReactionSection } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/earnings-recap/components/market-reaction-section/MarketReactionSection';

import { useEarningsInsightDrawer } from '../../useEarningsInsightDrawer';
import { BullBearPoints } from '../components/bull-bear-points/BullBearPoints';
import { Sidebar } from '../components/side-bar/Sidebar';
import { useSidebarItems } from '../components/side-bar/useSidebarItems';
import sharedStyles from '../EarningsInsightWidget.module.scss';
import { DocumentsAndCitations } from './components/documents-and-citations/DocumentsAndCitations';
import { EarningsHighlightsTable } from './components/earnings-highlights-table/EarningsHighlightsTable';
import { KpisTable } from './components/KpisTable/KpisTable';
import { ManagementCommentarySection } from './components/management-commentary-section/ManagementCommentarySection';
import { RecapOverview } from './components/recap-overview/RecapOverview';
import styles from './EarningsRecap.module.scss';

const styledLoaderAreas: SkeletonLoaderArea[] = [
  {
    styles: {
      width: '100%',
      height: '100px',
      borderRadius: '8px',
    },
  },
];

export interface EarningsRecapProps {
  expandCitations: boolean;
}

export const EarningsRecap = ({ expandCitations }: EarningsRecapProps) => {
  const { t } = useTranslation(['earnings']);
  const { earningsId, activeEntityTag } = useEarningsInsightDrawer();
  const containerRef = useRef<HTMLDivElement>(null);
  const { recapItems } = useSidebarItems();
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );

  const {
    data: entityData,
    isFetching: isEntityFetching,
    isError: isEntityError,
    error: entityError,
  } = useEntity(activeEntityTag ?? '');

  const {
    data: earningsInsightsData,
    isFetching: isEarningsInsightsFetching,
    isError: isEarningsInsightsError,
    error: earningsInsightsError,
  } = useEarningsInsightsFilter({
    body: {
      earnings_ids: [earningsId ?? ''],
    },
    isEnabled: !!earningsId,
  });

  const {
    data: earningsData,
    isFetching: isEarningsFetching,
    isError: isEarningsError,
    error: earningsError,
  } = useGetEarningById(earningsId ?? '');

  const { data: earningsRecapData, isFetching: isEarningsRecapFetching } =
    useGetEarningsInsightRecap(
      earningsInsightsData?.find(insight => insight.type === 'recap')?.id ?? ''
    );

  const isLoading =
    isEarningsFetching ||
    isEarningsRecapFetching ||
    isEarningsInsightsFetching ||
    isEntityFetching;

  const isError = isEarningsError || isEarningsInsightsError || isEntityError;

  if (isLoading) {
    return <SkeletonLoader areas={styledLoaderAreas} />;
  }

  if (isError || !entityData?.data) {
    throw new Error(
      earningsError?.message ||
        earningsInsightsError?.message ||
        entityError?.message
    );
  }

  const isReported = earningsData?.reported;

  if (!isReported) {
    return (
      <div className={sharedStyles.emptyContainer}>
        <div className={sharedStyles.emptyWrapper}>
          <img
            className={sharedStyles.emptyImage}
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <div className={sharedStyles.emptyTitle}>
            {t('earnings:earningsInsight.drawer.earningsRecap.emptyTitle')}
          </div>
        </div>
      </div>
    );
  }

  const entity = entityData.data;

  if (!earningsRecapData) {
    return (
      <div
        className={clsx(styles.rootLegacy, {
          [styles.rootLegacyUniversal]: isUniversalDrawerEnabled,
        })}
      >
        <div className={styles.tableWrapper}>
          <EarningsHighlightsTable earningsData={earningsData} />
        </div>
        <div className={sharedStyles.emptyWrapper}>
          <img
            className={sharedStyles.emptyImage}
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <div className={sharedStyles.emptyTitle}>
            {t(
              'earnings:earningsInsight.drawer.earningsRecap.earningsAreBeingAnalyzedOnQuarterYear',
              {
                fiscalPeriod: earningsData.fiscal_period,
                year: earningsData.fiscal_year,
              }
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.separator}>
        <div className={styles.leftContainer}>
          <div
            ref={containerRef}
            data-print-scrollable
            className={clsx(styles.scrollableContainer, {
              [styles.scrollableContainerFullWidth]: isUniversalDrawerEnabled,
            })}
          >
            <div
              ref={recapItems[0].ref}
              data-testid="overview"
              className={styles.section}
            >
              <RecapOverview
                title={earningsRecapData.title}
                activeEntityTag={activeEntityTag ?? ''}
                earningsData={earningsData}
                recapData={earningsRecapData}
                recapEarningsInsight={earningsInsightsData?.find(
                  insight => insight.type === 'recap'
                )}
              />
            </div>
            <div
              ref={recapItems[1].ref}
              data-testid="market-reaction"
              className={styles.section}
            >
              <div className={styles.historicalReactionWrapper}>
                <MarketReactionSection
                  entity={entity}
                  earningsId={earningsId ?? ''}
                  reportingDate={earningsData.reporting_date}
                />
              </div>
            </div>
            <div
              ref={recapItems[2].ref}
              data-testid="kpis-in-focus"
              className={styles.section}
            >
              <KpisTable kpisData={earningsRecapData.kpis} />
            </div>
            <div
              ref={recapItems[3].ref}
              data-testid="management-commentary"
              className={styles.section}
            >
              <ManagementCommentarySection
                managementInsights={earningsRecapData.insights}
                quotes={earningsRecapData.quotes}
                createdAt={earningsRecapData.created_at}
              />
            </div>
            <div
              ref={recapItems[4].ref}
              data-testid="thematic-focus"
              className={styles.section}
            >
              <ThematicFocus
                themes={earningsRecapData.themes}
                otherThemes={earningsRecapData.other_themes}
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.thematicFocusRecap'
                )}
                withPill
              />
            </div>
            <div
              ref={recapItems[5].ref}
              data-testid="related-companies-impact"
              className={styles.section}
            >
              <ErrorBoundaryWrapper>
                <ImpactedCompanies
                  impactedCompanies={earningsRecapData.impacted_companies}
                  variant="secondary"
                  title={t('earnings:earningsInsight.drawer.impactedCompanies')}
                  insightCreatedAt={earningsRecapData.created_at}
                />
              </ErrorBoundaryWrapper>
            </div>
            <div
              ref={recapItems[6].ref}
              data-testid="peer-comparison"
              className={styles.section}
            >
              <TextSection
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.peerComparison'
                )}
                variant="primary"
                useBulletPoints
                textContent={earningsRecapData.peer_comparison_sector_context}
              />
            </div>
            <div
              ref={recapItems[7].ref}
              data-testid="analyst-and-street"
              className={styles.section}
            >
              <BullBearPoints
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.analystAndStreet'
                )}
                data={earningsRecapData.analyst_and_street_reaction}
                variant="primary"
                useBulletPoints
              />
            </div>
            <div
              ref={recapItems[8].ref}
              data-testid="risk-assessment-update"
              className={styles.section}
            >
              <TextSection
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.riskAssessmentUpdate'
                )}
                variant="primary"
                useBulletPoints
                textContent={[
                  ...earningsRecapData.risk_assessment_update.assessments,
                  <span key="upcoming-catalysts">
                    <span className={styles.upcomingCatalysts}>
                      {t(
                        'earningsInsight.drawer.earningsRecap.upcomingCatalysts'
                      )}
                    </span>
                    <span>
                      {
                        earningsRecapData.risk_assessment_update
                          .upcoming_catalysts
                      }
                    </span>
                  </span>,
                ]}
              />
            </div>
            <div data-testid="citations" className={styles.section}>
              <DocumentsAndCitations
                earningsRecapData={earningsRecapData}
                expandCitations={expandCitations}
              />
            </div>
            <ScheduleDemoBanner />
          </div>
        </div>
        {!isUniversalDrawerEnabled && (
          <Sidebar containerRef={containerRef} items={recapItems} />
        )}
      </div>
    </div>
  );
};
